/**
 * Structured Content Editor for Blog V1 Articles
 * Manages sections with blocks (paragraph, lists, FAQ, callout, image, table)
 */

import React, { useState } from 'react';
import { TipTapEditor } from './TipTapEditor';
import { TipTapTitleEditor } from './TipTapTitleEditor';
import { getTitleJson, getTitlePlainText } from '../../utils/titleFormat';

// Simple UUID generator
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const SECTION_INTENTS = [
  { value: 'core_explanation', label: 'Core Explanation' },
  { value: 'definition', label: 'Definition' },
  { value: 'comparison', label: 'Comparison' },
  { value: 'step_by_step', label: 'Step by Step' },
  { value: 'example', label: 'Example' },
  { value: 'decision_support', label: 'Decision Support' },
  { value: 'faq', label: 'FAQ' },
  { value: 'reference', label: 'Reference' }
];

const BLOCK_TYPES = [
  { value: 'paragraph', label: 'Paragraph' },
  { value: 'bullet_list', label: 'Bullet List' },
  { value: 'numbered_list', label: 'Numbered List' },
  { value: 'faq', label: 'FAQ Item' },
  { value: 'callout', label: 'Callout' },
  { value: 'image', label: 'Image' },
  { value: 'table', label: 'Table' }
];

const CALLOUT_VARIANTS = [
  { value: 'info', label: 'Info' },
  { value: 'tip', label: 'Tip' },
  { value: 'warning', label: 'Warning' }
];

export const StructuredContentEditor = ({ contentSections = [], onChange }) => {
  const [sections, setSections] = useState(contentSections || []);
  const [collapsedSections, setCollapsedSections] = useState(new Set());

  const updateSections = (newSections) => {
    setSections(newSections);
    if (onChange) {
      onChange(newSections);
    }
  };

  const addSection = () => {
    const newSection = {
      id: generateUUID(),
      heading: { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: '' }] }] }, // TipTap JSON format
      intent: 'core_explanation',
      default_open: true,
      blocks: []
    };
    const newSections = [...sections, newSection];
    updateSections(newSections);
    // Collapse the newly added section by default for better UX
    setCollapsedSections(prev => new Set([...prev, newSection.id]));
  };

  const updateSection = (sectionId, field, value) => {
    updateSections(sections.map(s => 
      s.id === sectionId ? { ...s, [field]: value } : s
    ));
  };

  const removeSection = (sectionId) => {
    updateSections(sections.filter(s => s.id !== sectionId));
  };

  const addBlock = (sectionId, blockType) => {
    let newBlock = {};
    switch (blockType) {
      case 'paragraph':
        newBlock = {
          type: 'paragraph',
          content: {
            format: 'tiptap',
            json: { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: '' }] }] }
          }
        };
        break;
      case 'bullet_list':
        newBlock = { type: 'bullet_list', items: [''] };
        break;
      case 'numbered_list':
        newBlock = { type: 'numbered_list', items: [''] };
        break;
      case 'faq':
        newBlock = {
          type: 'faq',
          question: {
            format: 'tiptap',
            json: { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: '' }] }] }
          },
          answer: {
            format: 'tiptap',
            json: { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: '' }] }] }
          }
        };
        break;
      case 'callout':
        newBlock = {
          type: 'callout',
          variant: 'info',
          content: {
            format: 'tiptap',
            json: { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: '' }] }] }
          }
        };
        break;
      case 'image':
        newBlock = { type: 'image', url: '', alt: '', caption: '' };
        break;
      case 'table':
        newBlock = { type: 'table', headers: ['Header 1', 'Header 2'], rows: [['', '']] };
        break;
      default:
        return;
    }

    updateSections(sections.map(s => 
      s.id === sectionId ? { ...s, blocks: [...(s.blocks || []), newBlock] } : s
    ));
  };

  const updateBlock = (sectionId, blockIndex, field, value) => {
    updateSections(sections.map(s => {
      if (s.id === sectionId) {
        const newBlocks = [...(s.blocks || [])];
        newBlocks[blockIndex] = { ...newBlocks[blockIndex], [field]: value };
        return { ...s, blocks: newBlocks };
      }
      return s;
    }));
  };

  const removeBlock = (sectionId, blockIndex) => {
    updateSections(sections.map(s => {
      if (s.id === sectionId) {
        return { ...s, blocks: (s.blocks || []).filter((_, i) => i !== blockIndex) };
      }
      return s;
    }));
  };

  const moveBlock = (sectionId, blockIndex, direction) => {
    updateSections(sections.map(s => {
      if (s.id === sectionId && s.blocks) {
        const newBlocks = [...s.blocks];
        const newIndex = direction === 'up' ? blockIndex - 1 : blockIndex + 1;
        if (newIndex >= 0 && newIndex < newBlocks.length) {
          [newBlocks[blockIndex], newBlocks[newIndex]] = [newBlocks[newIndex], newBlocks[blockIndex]];
        }
        return { ...s, blocks: newBlocks };
      }
      return s;
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-[#111827]">Structured Content Sections</h3>
        <button
          type="button"
          onClick={addSection}
          className="px-4 py-2 bg-[#1E40AF] text-white text-sm font-semibold rounded-lg hover:bg-[#1e3a8a] transition-colors"
        >
          + Add Section
        </button>
      </div>

      {sections.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-[#E5E7EB] rounded-lg">
          <p className="text-[#6B7280] text-sm">No sections yet. Add your first section to get started.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sections.map((section, sectionIndex) => (
            <div key={section.id} className="border-2 border-[#E5E7EB] rounded-lg p-4 bg-[#F9FAFB]">
              {/* Section Header */}
              <div className="flex justify-between items-start mb-4 gap-4">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-[#111827] mb-1">Section Intent</label>
                    <select
                      value={section.intent || 'core_explanation'}
                      onChange={(e) => updateSection(section.id, 'intent', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:outline-none focus:border-[#1E40AF]"
                    >
                      {SECTION_INTENTS.map(intent => (
                        <option key={intent.value} value={intent.value}>{intent.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-[#111827] mb-1">Section Heading (Rich Text)</label>
                    <TipTapTitleEditor
                      content={typeof section.heading === 'string' ? getTitleJson(section.heading) : (section.heading || { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: '' }] }] })}
                      onChange={(json) => updateSection(section.id, 'heading', json)}
                      placeholder="Section heading (supports formatting)"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={section.default_open !== false}
                        onChange={(e) => updateSection(section.id, 'default_open', e.target.checked)}
                        className="w-4 h-4 text-[#1E40AF] border-[#E5E7EB] rounded focus:ring-[#1E40AF]"
                      />
                      <span className="text-xs font-semibold text-[#111827]">Open by default</span>
                    </label>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeSection(section.id)}
                  className="text-red-600 hover:text-red-800"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>

              {/* Add Block */}
              <div className="mb-3">
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      addBlock(section.id, e.target.value);
                      e.target.value = '';
                    }
                  }}
                  className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:outline-none focus:border-[#1E40AF]"
                >
                  <option value="">+ Add Block...</option>
                  {BLOCK_TYPES.map(block => (
                    <option key={block.value} value={block.value}>{block.label}</option>
                  ))}
                </select>
              </div>

              {/* Blocks */}
              <div className="space-y-3">
                {(section.blocks || []).map((block, blockIndex) => (
                  <div key={blockIndex} className="bg-white border border-[#E5E7EB] rounded p-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-semibold text-[#6B7280]">{block.type}</span>
                      <div className="flex items-center gap-2">
                        {blockIndex > 0 && (
                          <button
                            type="button"
                            onClick={() => moveBlock(section.id, blockIndex, 'up')}
                            className="text-[#6B7280] hover:text-[#111827]"
                          >
                            ↑
                          </button>
                        )}
                        {blockIndex < (section.blocks || []).length - 1 && (
                          <button
                            type="button"
                            onClick={() => moveBlock(section.id, blockIndex, 'down')}
                            className="text-[#6B7280] hover:text-[#111827]"
                          >
                            ↓
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeBlock(section.id, blockIndex)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    {/* Block Content */}
                    {block.type === 'paragraph' && (
                      <TipTapEditor
                        content={block.content?.json || { type: 'doc', content: [] }}
                        onChange={(json) => updateBlock(section.id, blockIndex, 'content', { format: 'tiptap', json })}
                        placeholder="Paragraph content..."
                      />
                    )}

                    {block.type === 'bullet_list' && (
                      <div className="space-y-2">
                        {(block.items || ['']).map((item, itemIndex) => (
                          <div key={itemIndex} className="flex gap-2">
                            <span className="mt-2 text-[#6B7280]">•</span>
                            <input
                              type="text"
                              value={item}
                              onChange={(e) => {
                                const newItems = [...(block.items || [])];
                                newItems[itemIndex] = e.target.value;
                                updateBlock(section.id, blockIndex, 'items', newItems);
                              }}
                              className="flex-1 px-2 py-1 text-sm border border-[#E5E7EB] rounded focus:outline-none focus:border-[#1E40AF]"
                              placeholder={`Item ${itemIndex + 1}`}
                            />
                            {(block.items || []).length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newItems = (block.items || []).filter((_, i) => i !== itemIndex);
                                  updateBlock(section.id, blockIndex, 'items', newItems);
                                }}
                                className="text-red-600 hover:text-red-800"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => updateBlock(section.id, blockIndex, 'items', [...(block.items || []), ''])}
                          className="text-xs text-[#1E40AF] hover:text-[#1e3a8a]"
                        >
                          + Add Item
                        </button>
                      </div>
                    )}

                    {block.type === 'numbered_list' && (
                      <div className="space-y-2">
                        {(block.items || ['']).map((item, itemIndex) => (
                          <div key={itemIndex} className="flex gap-2">
                            <span className="mt-2 text-[#6B7280] w-6">{itemIndex + 1}.</span>
                            <input
                              type="text"
                              value={item}
                              onChange={(e) => {
                                const newItems = [...(block.items || [])];
                                newItems[itemIndex] = e.target.value;
                                updateBlock(section.id, blockIndex, 'items', newItems);
                              }}
                              className="flex-1 px-2 py-1 text-sm border border-[#E5E7EB] rounded focus:outline-none focus:border-[#1E40AF]"
                              placeholder={`Step ${itemIndex + 1}`}
                            />
                            {(block.items || []).length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newItems = (block.items || []).filter((_, i) => i !== itemIndex);
                                  updateBlock(section.id, blockIndex, 'items', newItems);
                                }}
                                className="text-red-600 hover:text-red-800"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => updateBlock(section.id, blockIndex, 'items', [...(block.items || []), ''])}
                          className="text-xs text-[#1E40AF] hover:text-[#1e3a8a]"
                        >
                          + Add Item
                        </button>
                      </div>
                    )}

                    {block.type === 'faq' && (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-semibold text-[#111827] mb-1">Question</label>
                          <TipTapEditor
                            content={block.question?.json || { type: 'doc', content: [] }}
                            onChange={(json) => updateBlock(section.id, blockIndex, 'question', { format: 'tiptap', json })}
                            placeholder="FAQ question..."
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-[#111827] mb-1">Answer</label>
                          <TipTapEditor
                            content={block.answer?.json || { type: 'doc', content: [] }}
                            onChange={(json) => updateBlock(section.id, blockIndex, 'answer', { format: 'tiptap', json })}
                            placeholder="FAQ answer..."
                          />
                        </div>
                      </div>
                    )}

                    {block.type === 'callout' && (
                      <div className="space-y-2">
                        <select
                          value={block.variant || 'info'}
                          onChange={(e) => updateBlock(section.id, blockIndex, 'variant', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-[#E5E7EB] rounded focus:outline-none focus:border-[#1E40AF]"
                        >
                          {CALLOUT_VARIANTS.map(v => (
                            <option key={v.value} value={v.value}>{v.label}</option>
                          ))}
                        </select>
                        <TipTapEditor
                          content={block.content?.json || { type: 'doc', content: [] }}
                          onChange={(json) => updateBlock(section.id, blockIndex, 'content', { format: 'tiptap', json })}
                          placeholder="Callout content..."
                        />
                      </div>
                    )}

                    {block.type === 'image' && (
                      <div className="space-y-2">
                        <input
                          type="url"
                          value={block.url || ''}
                          onChange={(e) => updateBlock(section.id, blockIndex, 'url', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-[#E5E7EB] rounded focus:outline-none focus:border-[#1E40AF]"
                          placeholder="Image URL"
                        />
                        <input
                          type="text"
                          value={block.alt || ''}
                          onChange={(e) => updateBlock(section.id, blockIndex, 'alt', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-[#E5E7EB] rounded focus:outline-none focus:border-[#1E40AF]"
                          placeholder="Alt text"
                        />
                        <input
                          type="text"
                          value={block.caption || ''}
                          onChange={(e) => updateBlock(section.id, blockIndex, 'caption', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-[#E5E7EB] rounded focus:outline-none focus:border-[#1E40AF]"
                          placeholder="Caption (optional)"
                        />
                      </div>
                    )}

                    {block.type === 'table' && (
                      <div className="space-y-2">
                        <p className="text-xs text-[#6B7280]">Table editing - basic implementation. Edit JSON directly if needed.</p>
                        <textarea
                          value={JSON.stringify(block, null, 2)}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              updateBlock(section.id, blockIndex, '', parsed);
                            } catch (err) {
                              // Invalid JSON, ignore
                            }
                          }}
                          className="w-full px-2 py-1 text-xs font-mono border border-[#E5E7EB] rounded focus:outline-none focus:border-[#1E40AF]"
                          rows="6"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StructuredContentEditor;
